// Slack import panel — admin UI to bring a Slack workspace export
// into Nuggets Messenger.  Drop into Admin → Import Data.
//
// Uses src/lib/slackImport.js to parse the ZIP in-browser and drive
// the phased server endpoint (api/slack-import.js).  Shows a live
// progress bar per phase.

import React from 'react';
import { Upload, FileArchive, CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import { importSlackZip } from '../../lib/slackImport.js';

const NAVY   = '#0D1B2A';
const ORANGE = '#E87722';
const GREEN  = '#22C55E';
const RED    = '#EF4444';
const TEXT   = '#F0F4F8';
const MUTED  = 'rgba(255,255,255,0.55)';
const BORDER = 'rgba(255,255,255,0.10)';

const STAGE_LABELS = {
  init:     'Starting import',
  users:    'Matching users',
  channels: 'Creating channels',
  messages: 'Loading messages',
  done:     'Complete',
};

function bytesLabel(bytes) {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1)    return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

export default function SlackImportPanel() {
  const [file,     setFile]     = React.useState(null);
  const [running,  setRunning]  = React.useState(false);
  const [stage,    setStage]    = React.useState(null);
  const [done,     setDone]     = React.useState(0);
  const [total,    setTotal]    = React.useState(0);
  const [error,    setError]    = React.useState(null);
  const [summary,  setSummary]  = React.useState(null);

  const pickerRef = React.useRef(null);

  const onPick = (f) => {
    if (!f) return;
    if (!f.name.endsWith('.zip')) { setError('Not a .zip file.'); return; }
    setError(null);
    setSummary(null);
    setFile(f);
  };

  const start = async () => {
    if (!file || running) return;
    setRunning(true);
    setStage('init');
    setDone(0);
    setTotal(1);
    setError(null);
    setSummary(null);
    try {
      const result = await importSlackZip(file, {
        onProgress: (s, d, t) => {
          setStage(s);
          setDone(d);
          setTotal(t || 1);
        },
      });
      setSummary(result);
      setStage('done');
    } catch (err) {
      console.error('[slack-import]', err);
      setError(err.message || 'Import failed');
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setFile(null);
    setRunning(false);
    setStage(null);
    setDone(0);
    setTotal(0);
    setError(null);
    setSummary(null);
    if (pickerRef.current) pickerRef.current.value = '';
  };

  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;

  return (
    <div style={{
      background: NAVY, border: `1px solid ${BORDER}`, borderRadius: 10,
      padding: 18, color: TEXT, fontFamily: "'Montserrat',sans-serif",
      maxWidth: 640,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <FileArchive size={16} color={ORANGE} />
        <span style={{ fontSize: 10, letterSpacing: '0.14em', color: ORANGE, fontWeight: 800, textTransform: 'uppercase' }}>
          Slack Import
        </span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 6 }}>
        Bring your Slack workspace into Messenger
      </div>
      <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.55, marginBottom: 16 }}>
        Upload the <code style={{ color: TEXT }}>slack-export.zip</code> from your
        workspace (Workspace settings → Import/Export Data → Export). We&rsquo;ll
        match users by email, create <code style={{ color: TEXT }}>slack-*</code>
        channels for every public and private channel, and stream messages in as
        history — threads, reactions, and file references included. Re-runs are
        idempotent; nothing gets duplicated.
      </div>

      {/* File picker */}
      {!file && !summary && (
        <label
          htmlFor="slack-zip"
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 10, padding: '32px 20px',
            border: `2px dashed ${BORDER}`, borderRadius: 10,
            cursor: 'pointer', background: 'rgba(255,255,255,0.02)',
          }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; onPick(f); }}
        >
          <Upload size={22} color={MUTED} />
          <div style={{ color: TEXT, fontWeight: 700, fontSize: 13 }}>
            Drop the ZIP here, or click to browse
          </div>
          <div style={{ color: MUTED, fontSize: 11 }}>
            Files stay on your machine; the server only receives parsed batches.
          </div>
          <input
            ref={pickerRef}
            id="slack-zip"
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'none' }}
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </label>
      )}

      {/* Ready state */}
      {file && !running && !summary && (
        <div style={{ padding: 14, border: `1px solid ${BORDER}`, borderRadius: 10, background: 'rgba(255,255,255,0.03)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <FileArchive size={18} color={ORANGE} />
            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{file.name}</div>
              <div style={{ fontSize: 10, color: MUTED }}>{bytesLabel(file.size)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={start}
              style={{
                background: ORANGE, color: '#fff', border: 'none',
                padding: '9px 18px', borderRadius: 6,
                fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Start import
            </button>
            <button
              onClick={reset}
              style={{
                background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`,
                padding: '9px 12px', borderRadius: 6,
                fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Pick different file
            </button>
          </div>
        </div>
      )}

      {/* Progress state */}
      {running && stage && (
        <div style={{ padding: 14, border: `1px solid ${BORDER}`, borderRadius: 10, background: 'rgba(255,255,255,0.03)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{
              width: 14, height: 14, borderRadius: '50%',
              border: `2px solid ${ORANGE}`, borderTopColor: 'transparent',
              animation: 'spin 0.8s linear infinite',
            }} />
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              {STAGE_LABELS[stage] || stage}…
            </div>
            <div style={{ marginLeft: 'auto', fontSize: 11, color: MUTED }}>
              {done}/{total || '?'}
            </div>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: ORANGE, transition: 'width 0.2s ease',
            }} />
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Success */}
      {summary && (
        <div style={{ padding: 14, border: `1px solid ${GREEN}55`, borderRadius: 10, background: 'rgba(34,197,94,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <CheckCircle2 size={18} color={GREEN} />
            <div style={{ fontSize: 13, fontWeight: 700, color: GREEN }}>Import complete</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: 12 }}>
            <div>
              <div style={{ color: MUTED, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Channels</div>
              <div style={{ color: TEXT, fontWeight: 800, fontSize: 16 }}>{summary.counts.channels ?? '—'}</div>
            </div>
            <div>
              <div style={{ color: MUTED, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Roots</div>
              <div style={{ color: TEXT, fontWeight: 800, fontSize: 16 }}>{summary.counts.inserted_roots ?? '—'}</div>
            </div>
            <div>
              <div style={{ color: MUTED, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Replies</div>
              <div style={{ color: TEXT, fontWeight: 800, fontSize: 16 }}>{summary.counts.inserted_replies ?? '—'}</div>
            </div>
          </div>
          {summary.counts.skipped > 0 && (
            <div style={{ marginTop: 10, fontSize: 11, color: MUTED }}>
              {summary.counts.skipped} rows skipped (already imported, unmatched author, or non-user events).
            </div>
          )}
          <button
            onClick={reset}
            style={{
              marginTop: 12,
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`,
              padding: '7px 12px', borderRadius: 6,
              fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            <RotateCcw size={12} /> Import another
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ marginTop: 12, padding: 12, border: `1px solid ${RED}55`, borderRadius: 8, background: 'rgba(239,68,68,0.08)', color: RED, fontSize: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>{error}</div>
        </div>
      )}
    </div>
  );
}
